import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticle, incrementArticleView } from '../api/articlesApi';
import { useAuth } from '../contexts/AuthContext';
import ScrollControls from '../components/ScrollControls';
import { useLanguage } from '../hooks/useLanguage';
import { useTheme } from '../contexts/ThemeContext';

function getRoles(currentUser) {
  const rawRoles = currentUser?.roles || currentUser?.authorities || [];

  return rawRoles.map((role) => {
    if (typeof role === 'string') return role;
    return role.authority || role.name || '';
  });
}

function formatDate(value, lang) {
  if (!value) return null;

  try {
    return new Date(value).toLocaleDateString(lang === 'EN' ? 'en-US' : 'ru-RU');
  } catch {
    return null;
  }
}

function normalizeArticleContent(content) {
  if (!content) return '';

  /*
   * Older articles were saved as complete HTML documents. When such markup is
   * mounted inside the application, global html/body rules leak into the site
   * and can repaint or resize the page around the article. Keep article-local
   * styles, but remove the document shell and its global page rules.
   */
  return content
    .replace(/<!doctype[^>]*>/gi, '')
    .replace(/<meta\b[^>]*>/gi, '')
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '')
    .replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (match, attributes, css) => {
      const articleCss = css.replace(
        /(^|})\s*(?:html\s*,\s*body|body\s*,\s*html|html|body)\s*\{[^{}]*\}/gim,
        '$1',
      );

      return articleCss.trim() ? `<style${attributes}>${articleCss}</style>` : '';
    })
    .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, '');
}

const labels = {
  RU: {
    back: '← К списку статей',
    loading: 'Загрузка…',
    error: 'Ошибка загрузки статьи.',
    views: 'Просмотров',
    published: 'Опубликовано',
    updated: 'Обновлено',
    status: 'Статус',
    edit: 'Редактировать',
    noTitle: 'Без названия',
    article: 'Статья',
  },
  EN: {
    back: '← Back to articles',
    loading: 'Loading…',
    error: 'Failed to load article.',
    views: 'Views',
    published: 'Published',
    updated: 'Updated',
    status: 'Status',
    edit: 'Edit',
    noTitle: 'Untitled',
    article: 'Article',
  },
};

function GlassPanel({ isDark, className = '', children, allowOverflow = false }) {
  return (
    <div
      className={[
        'article-panel relative border',
        allowOverflow ? 'overflow-visible' : 'overflow-hidden',
        'transition-colors duration-300',
        isDark
          ? 'border-white/10 bg-gray-900/35 ring-1 ring-white/10'
          : 'border-gray-200 bg-white/80 ring-1 ring-black/5',
        className,
      ].join(' ')}
    >
      <div className="relative z-[1]">{children}</div>
    </div>
  );
}

function initSokolovTimeline(root) {
  if (!root) return () => {};

  const nav = root.querySelector('[data-sokolov-timeline]');
  const marker = root.querySelector('[data-sokolov-marker]');

  // Обычные статьи этих элементов не содержат — для них ничего не делаем.
  if (!nav || !marker) return () => {};

  const links = [...nav.querySelectorAll('a[data-period]')];
  const sections = [...root.querySelectorAll('.section[data-period]')];

  if (!links.length || !sections.length) return () => {};

  const linkByPeriod = new Map(
    links.map((link) => [link.dataset.period, link]),
  );

  /*
   * У одного периода может быть несколько секций.
   * Например, 1916 год разделён на ТОЗ и ОРЗ, но на шкале это один пункт.
   */
  const sectionsByPeriod = new Map();

  for (const section of sections) {
    const period = section.dataset.period;
    if (!period) continue;

    if (!sectionsByPeriod.has(period)) {
      sectionsByPeriod.set(period, []);
    }

    sectionsByPeriod.get(period).push(section);
  }

  const periods = links
    .map((link) => link.dataset.period)
    .filter((period) => period && sectionsByPeriod.has(period));

  if (!periods.length) return () => {};

  let ranges = [];
  let activePeriod = null;

  let targetMarkerX = 0;
  let markerX = 0;

  let targetNavScroll = nav.scrollLeft;
  let navScroll = nav.scrollLeft;

  let animationFrame = 0;
  let recalcFrame = 0;

  const clamp = (value, min, max) =>
    Math.max(min, Math.min(max, value));

  const lerp = (from, to, amount) =>
    from + (to - from) * amount;

  const smoothstep = (value) =>
    value * value * (3 - 2 * value);

  const setActivePeriod = (period) => {
    if (!period || activePeriod === period) return;

    activePeriod = period;

    for (const link of links) {
      if (link.dataset.period === period) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  };

  const recalculateRanges = () => {
    const probeOffset = Math.min(220, window.innerHeight * 0.28);
    const maxPageScroll = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );

    const periodData = periods
      .map((period) => {
        const periodSections = sectionsByPeriod.get(period) || [];
        const firstSection = periodSections[0];
        const lastSection = periodSections[periodSections.length - 1];
        const link = linkByPeriod.get(period);

        if (!firstSection || !lastSection || !link) return null;

        const firstTop =
          firstSection.getBoundingClientRect().top + window.scrollY;

        const lastBottom =
          lastSection.getBoundingClientRect().bottom + window.scrollY;

        return {
          period,
          link,
          firstSection,
          lastSection,
          startScroll: clamp(
            firstTop - probeOffset,
            0,
            maxPageScroll,
          ),
          naturalEndScroll: clamp(
            lastBottom - probeOffset,
            0,
            maxPageScroll,
          ),
          linkLeft: link.offsetLeft,
          linkRight: link.offsetLeft + link.offsetWidth,
        };
      })
      .filter(Boolean);

    if (!periodData.length) {
      ranges = [];
      return;
    }

    /*
     * Границы по X делим между соседними кнопками.
     * Поэтому палочка:
     *   - начинается у левого края 1909;
     *   - пока читается 1914, не заходит внутрь кнопки 1915;
     *   - заканчивается у правого края 1917.
     * При этом между периодами нет визуального скачка.
     */
    const xBoundaries = new Array(periodData.length + 1);
    xBoundaries[0] = periodData[0].linkLeft;

    for (let index = 1; index < periodData.length; index += 1) {
      const previous = periodData[index - 1];
      const current = periodData[index];

      xBoundaries[index] =
        (previous.linkRight + current.linkLeft) / 2;
    }

    xBoundaries[periodData.length] =
      periodData[periodData.length - 1].linkRight;

    /*
     * Каждый год активен строго до начала следующего года.
     * Никакого переключения "по ближайшему центру" больше нет.
     */
    ranges = periodData.map((item, index) => {
      const next = periodData[index + 1];

      let endScroll = next
        ? next.startScroll
        : item.naturalEndScroll;

      // Последний период должен иметь достижимый конец.
      if (!next && endScroll <= item.startScroll) {
        endScroll = maxPageScroll;
      }

      // Защита от одинаковых координат на очень короткой странице.
      endScroll = Math.max(item.startScroll + 1, endScroll);

      return {
        ...item,
        endScroll,
        startX: xBoundaries[index],
        endX: xBoundaries[index + 1],
      };
    });
  };

  const updateTargets = () => {
    if (!ranges.length) {
      recalculateRanges();
    }

    if (!ranges.length) return;

    const maxPageScroll = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight,
    );

    const pageY = clamp(window.scrollY, 0, maxPageScroll);

    let currentRange = ranges[0];

    if (pageY >= ranges[ranges.length - 1].endScroll) {
      currentRange = ranges[ranges.length - 1];
    } else {
      for (let index = ranges.length - 1; index >= 0; index -= 1) {
        if (pageY >= ranges[index].startScroll) {
          currentRange = ranges[index];
          break;
        }
      }
    }

    let rawProgress;

    if (pageY <= currentRange.startScroll) {
      rawProgress = 0;
    } else if (pageY >= currentRange.endScroll) {
      rawProgress = 1;
    } else {
      rawProgress =
        (pageY - currentRange.startScroll) /
        Math.max(1, currentRange.endScroll - currentRange.startScroll);
    }

    const progress = smoothstep(clamp(rawProgress, 0, 1));

    targetMarkerX = lerp(
      currentRange.startX,
      currentRange.endX,
      progress,
    );

    // Активен именно тот раздел, который сейчас читается.
    setActivePeriod(currentRange.period);

    const maxNavScroll = Math.max(
      0,
      nav.scrollWidth - nav.clientWidth,
    );

    targetNavScroll =
      maxNavScroll === 0
        ? 0
        : clamp(
            targetMarkerX - nav.clientWidth / 2,
            0,
            maxNavScroll,
          );
  };

  const render = () => {
    updateTargets();

    markerX = lerp(markerX, targetMarkerX, 0.22);
    navScroll = lerp(navScroll, targetNavScroll, 0.18);

    marker.style.transform =
      `translate3d(${markerX - 1}px, 0, 0)`;

    if (Math.abs(nav.scrollLeft - navScroll) > 0.2) {
      nav.scrollLeft = navScroll;
    }

    const markerIsMoving =
      Math.abs(markerX - targetMarkerX) > 0.05;

    const navIsMoving =
      Math.abs(navScroll - targetNavScroll) > 0.05;

    if (markerIsMoving || navIsMoving) {
      animationFrame = requestAnimationFrame(render);
    } else {
      markerX = targetMarkerX;
      navScroll = targetNavScroll;

      marker.style.transform =
        `translate3d(${markerX - 1}px, 0, 0)`;

      nav.scrollLeft = navScroll;
      animationFrame = 0;
    }
  };

  const requestRender = () => {
    updateTargets();

    if (!animationFrame) {
      animationFrame = requestAnimationFrame(render);
    }
  };

  const scheduleRecalculate = () => {
    if (recalcFrame) {
      cancelAnimationFrame(recalcFrame);
    }

    recalcFrame = requestAnimationFrame(() => {
      recalcFrame = 0;
      recalculateRanges();
      requestRender();
    });
  };

  const handleScroll = () => {
    requestRender();
  };

  const handleResize = () => {
    scheduleRecalculate();
  };

  window.addEventListener('scroll', handleScroll, {
    passive: true,
  });
  window.addEventListener('resize', handleResize);

  const clickHandlers = [];

  for (const link of links) {
    const handleClick = (event) => {
      const period = link.dataset.period;
      const firstSection = sectionsByPeriod.get(period)?.[0];

      if (!firstSection) return;

      event.preventDefault();

      firstSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      setActivePeriod(period);
      requestRender();
    };

    link.addEventListener('click', handleClick);
    clickHandlers.push([link, handleClick]);
  }

  /*
   * Картинки меняют высоту статьи после загрузки.
   */
  const imageHandlers = [];

  for (const image of root.querySelectorAll('img')) {
    if (image.complete) continue;

    const handleLoad = () => scheduleRecalculate();

    image.addEventListener('load', handleLoad);
    image.addEventListener('error', handleLoad);

    imageHandlers.push([image, handleLoad]);
  }

  /*
   * Сюда же попадают раскрытие <details> и любые другие изменения высоты.
   */
  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => scheduleRecalculate())
      : null;

  resizeObserver?.observe(root);

  recalculateRanges();
  updateTargets();

  // На первом кадре сразу ставим палочку в правильную позицию без прилёта слева.
  markerX = targetMarkerX;
  navScroll = targetNavScroll;

  marker.style.transform =
    `translate3d(${markerX - 1}px, 0, 0)`;
  nav.scrollLeft = navScroll;

  requestRender();

  return () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleResize);

    for (const [link, handleClick] of clickHandlers) {
      link.removeEventListener('click', handleClick);
    }

    for (const [image, handleLoad] of imageHandlers) {
      image.removeEventListener('load', handleLoad);
      image.removeEventListener('error', handleLoad);
    }

    resizeObserver?.disconnect();

    if (animationFrame) {
      cancelAnimationFrame(animationFrame);
    }

    if (recalcFrame) {
      cancelAnimationFrame(recalcFrame);
    }
  };
}

export default function ArticlePage() {
  const { slug } = useParams();
  const { currentUser } = useAuth();
  const { lang } = useLanguage();
  const { isDark } = useTheme();

  const roles = getRoles(currentUser);
  const isAdmin = roles.includes('ROLE_ADMIN') || roles.includes('ADMIN');

  const [article, setArticle] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const articleContentRef = useRef(null);

  const t = labels[lang] || labels.RU;

  const uploadHost =
    process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';

  const coverPhotoUrl = useMemo(() => {
    if (!article?.coverPhotoUrl) return null;

    return article.coverPhotoUrl.startsWith('/')
      ? `${uploadHost}${article.coverPhotoUrl}`
      : article.coverPhotoUrl;
  }, [article?.coverPhotoUrl, uploadHost]);

  const articleHtml = useMemo(
    () => ({ __html: normalizeArticleContent(article?.content) }),
    [article?.content],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!slug || slug === 'undefined') return;

    const viewKey = `article-viewed:${slug}`;

    if (sessionStorage.getItem(viewKey)) return;

    sessionStorage.setItem(viewKey, 'true');

    incrementArticleView(slug)
      .then(() => {
        setArticle((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            viewCount: (prev.viewCount ?? 0) + 1,
          };
        });
      })
      .catch((err) => {
        console.error('Failed to increment article view:', err);
      });
  }, [slug]);

  useEffect(() => {
    if (!slug || slug === 'undefined') {
      setError(new Error('Article slug is missing'));
      setLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await getArticle(slug, lang);

        if (!mounted) return;

        setArticle(data);
      } catch (err) {
        console.error(err);

        if (!mounted) return;

        setError(err);
        setArticle(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [slug, lang]);

  useEffect(() => {
    if (!article?.content || !articleContentRef.current) {
      return undefined;
    }

    return initSokolovTimeline(articleContentRef.current);
  }, [article?.content, slug, lang, isDark]);

  const pageClass = 'article-page';

  const mutedTextClass = isDark ? 'text-gray-300' : 'text-gray-700';
  const secondaryTextClass = isDark ? 'text-gray-400' : 'text-gray-500';

  const proseClass = isDark
    ? 'prose prose-invert max-w-none text-gray-200'
    : 'prose max-w-none text-gray-800';

  const backButtonClass = 'secondary-button article-back';

  if (loading) {
    return (
      <div className={pageClass}>
        <div className="mx-auto max-w-5xl px-2 py-4 sm:p-6">
          <p className={mutedTextClass}>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className={pageClass}>
        <div className="mx-auto max-w-5xl px-2 py-4 sm:p-6">
          <p className="text-red-400">{t.error}</p>

          <Link to="/articles" className={`mt-4 ${backButtonClass}`}>
            {t.back}
          </Link>
        </div>
      </div>
    );
  }

  const publishedDate = formatDate(article.publishedAt, lang);
  const updatedDate = formatDate(article.updatedAt, lang);

  return (
    <div className={pageClass}>
      <div className="article-page__inner relative mx-auto max-w-5xl px-2 py-4 sm:px-6 sm:py-8">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-20 left-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl"
        />

        <div className="relative">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex justify-start sm:block">
              <Link to="/articles" className={backButtonClass}>
                {t.back}
              </Link>
            </div>

            <div className="flex w-full flex-wrap items-center justify-end gap-3 sm:w-auto">
              {isAdmin && (
                <Link
                  to={`/admin/articles/${article.id}/edit`}
                  className="rounded-2xl border border-blue-400/30 bg-blue-600/90 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700"
                >
                  {t.edit}
                </Link>
              )}
            </div>
          </div>

          <GlassPanel isDark={isDark} className="p-4 sm:p-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                <div
                  className={[
                    'mb-3 inline-flex rounded-full border px-3 py-1 text-xs font-semibold tracking-wide',
                    isDark
                      ? 'border-white/10 bg-white/5 text-indigo-200'
                      : 'border-indigo-100 bg-indigo-50 text-indigo-700',
                  ].join(' ')}
                >
                  {t.article}
                </div>

                <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
                  {article.title || t.noTitle}
                </h1>

                {article.summary && (
                  <p className={`mt-3 whitespace-pre-wrap text-lg italic ${mutedTextClass}`}>
                    {article.summary}
                  </p>
                )}

                <div className={`mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm ${secondaryTextClass}`}>
                  <span>
                    {t.views}: {article.viewCount ?? 0}
                  </span>

                  {publishedDate && (
                    <span>
                      {t.published}: {publishedDate}
                    </span>
                  )}

                  {!publishedDate && updatedDate && (
                    <span>
                      {t.updated}: {updatedDate}
                    </span>
                  )}

                  {isAdmin && article.status && (
                    <span>
                      {t.status}: {article.status}
                    </span>
                  )}
                </div>
              </div>

              {coverPhotoUrl && (
                <div className="shrink-0 sm:w-32 md:w-40">
                  <div
                    className={[
                      'overflow-hidden rounded-2xl border shadow-md transition-colors duration-300',
                      isDark ? 'border-white/10 bg-black/20' : 'border-gray-200 bg-white',
                    ].join(' ')}
                  >
                    <img
                      src={coverPhotoUrl}
                      alt={article.title || 'Article cover'}
                      className="h-28 w-full object-cover sm:h-32"
                    />
                  </div>
                </div>
              )}
            </div>
          </GlassPanel>

          <GlassPanel isDark={isDark} allowOverflow className="mt-5 p-3 sm:mt-6 sm:p-7 md:p-8">
            <article>
              <div
                ref={articleContentRef}
                className={proseClass}
                dangerouslySetInnerHTML={articleHtml}
              />
            </article>
          </GlassPanel>
        </div>
      </div>

      <ScrollControls />
    </div>
  );
}
